import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import FormattedNumberWithCurrency from "components/formattedNumberWithCurrencies/formattedNumberWithCurrency";

import {ProductShowContext} from "../../../../context/ProductShowContext";

import {getTranslation} from "domain/helpers/translations";

import store from "store";

import {MapperFields} from "utils/mapperFields";

JournalValue.propTypes = {
    field: PropTypes.string,
    oldValue: PropTypes.string,
    newValue: PropTypes.string
};

const Actions = {
    UPDATE: 'update',
    LOCK: 'lock',
    UNLOCK: 'unlock'
}

const PriceFields = [
    MapperFields.PRICE,
    MapperFields.OUTLET_PRICE,
    MapperFields.DISCOUNTED_PRICE,
    MapperFields.ECO_TAX
];

const BoolFields = [
    MapperFields.PRIVATE_PRODUCT,
    MapperFields.PRIVATE_PRICE,
    MapperFields.CATALOG_ONLY,
    MapperFields.HIDDEN_PRICE,
    MapperFields.STATUS
]

function resolveGenderLabel(genderList, id) {
    // Le journal stocke l'UUID brut (Mapper::serializeValue() fait $id->toString()), pas l'IRI
    // ('@id') utilisée ailleurs dans les formulaires : comparaison sur le champ 'id' brut.
    const gender = genderList.find((gender) => gender.id === id);

    return gender ? getTranslation(gender).value : null;
}

function resolveBrandLabel(brandList, id) {
    return brandList.find((brand) => brand.id === id)?.name ?? null;
}

function resolveTaxRateLabel(taxRateList, id) {
    const taxRate = taxRateList.find((taxRate) => taxRate.id === id)?.value;

    return taxRate !== undefined ? `${taxRate}%` : null;
}

function formatValue(action = Actions.UPDATE, field, value = null, currency = null, lists = {}) {
    if (action === Actions.LOCK || action === Actions.UNLOCK) {
        if (value === '0') {
            return <FormattedMessage id='product.show.modificationLog.value.unlocked' />
        }

        if (value === '1') {
            return <FormattedMessage id='product.show.modificationLog.value.locked' />
        }
    }

    if (BoolFields.includes(field)) {
        if (value === '0') {
            return <FormattedMessage id='product.show.modificationLog.value.disabled' />
        }

        if (value === '1') {
            return <FormattedMessage id='product.show.modificationLog.value.enabled' />
        }
    }

    if (value !== null && PriceFields.includes(field) && currency !== null) {
        // value comes from API as e string. NumberFormat
        // "." is then not recognized as a FR separator
        return <FormattedNumberWithCurrency value={Number(value)} currency={currency} />
    }

    if (value !== null && field === MapperFields.GENDER) {
        return resolveGenderLabel(lists.genderList ?? [], value) ?? value;
    }

    if (value !== null && field === MapperFields.BRAND) {
        return resolveBrandLabel(lists.brandList ?? [], value) ?? value;
    }

    if (value !== null && field === MapperFields.TAX_RATE) {
        return resolveTaxRateLabel(lists.taxRateList ?? [], value) ?? value;
    }

    return value;
}

function JournalValue({action = Actions.UPDATE, field, oldValue = null, newValue = null}) {
    const currentOrganization = store.getState()?.currentOrganization?.retrieved;
    const currency = currentOrganization?.currency;
    const {genderList, brandList, taxRateList} = useContext(ProductShowContext);

    if (oldValue === null && newValue === null) {
        return null;
    }

    const lists = {genderList, brandList, taxRateList};

    oldValue = formatValue(action, field, oldValue, currency, lists);
    newValue = formatValue(action, field, newValue, currency, lists);

    if (oldValue === null && newValue !== null) {
        return newValue;
    }

    if (oldValue !== null && newValue === null) {
        return <s>{oldValue}</s>
    }

    if (oldValue && newValue) {
        return <div>{oldValue} → {newValue}</div>;
    }

    return (
        <div>{oldValue} → {newValue}</div>
    )
}

export default JournalValue;
